import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const AboutPage = () => {
  // SEO metadata based on the SEO strategy document
  const seoData = {
    title: "About Dozzy | Evidence-Based AI Sleep Consultant for Babies",
    description: "Learn how Dozzy combines AI technology with trusted Australian sleep guidelines to create affordable, personalized sleep plans for babies and toddlers.",
    keywords: [
      "evidence-based sleep training", 
      "Australian sleep guidelines", 
      "AI sleep consultant",
      "baby sleep technology",
      "personalized sleep plans",
      "affordable sleep consultant",
      "Red Nose sleep guidelines"
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Dozzy.au",
      "url": "https://dozzy.au",
      "logo": "https://dozzy.au/images/logo.png",
      "description": "AI-powered sleep plans personalized to your child's unique needs, combining cutting-edge technology with Australia's most trusted sleep guidelines.",
      "foundingDate": "2022",
      "founders": [
        {
          "@type": "Person",
          "name": "Dr. Jane Doe"
        },
        {
          "@type": "Person",
          "name": "John Smith"
        }
      ]
    }
  };

  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        structuredData={seoData.structuredData}
        canonicalUrl="https://dozzy.au/about"
        ogType="website"
      />
      
      {/* Page Header */}
      <section className="py-5 bg-primary text-white text-center">
        <div className="container py-2">
          <h1>About Dozzy</h1>
          <p className="mx-auto" style={{ maxWidth: '700px' }}>
            Combining advanced AI technology with trusted Australian sleep guidelines to create personalized sleep plans for children.
          </p>
        </div>
      </section>
      
      {/* Our Story Section */}
      <section className="py-5">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <h2>Our Mission</h2>
              <p>
                Dozzy.au was developed in response to a clear need identified within the parenting community. As industry professionals with parenting experience, we recognized the challenges many families face in establishing healthy sleep patterns for their children.
              </p>
              <p>
                After conducting extensive research into various sleep methodologies and consulting with sleep specialists, we identified an opportunity to create an accessible, affordable solution based on trusted guidelines from respected Australian organizations.
              </p>
              <p>
                We combine advanced artificial intelligence technology with evidence-informed guidelines to create personalized sleep plans for children from newborn to 5 years of age. Our platform analyzes your child's unique sleep patterns, habits, and challenges to generate customized recommendations that align with your parenting style and your child's developmental stage.
              </p>
            </div>
            <div className="col-lg-6">
              <img src="/images/bastien-jaillot-eJwSOguD1rE-unsplash.jpg" alt="Parent with sleeping baby" className="img-fluid rounded-4 shadow" />
            </div>
          </div>
        </div>
      </section>
      
      {/* Sleep Deprivation Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 order-lg-2">
              <h2>Our Commitment to Safety</h2>
              <p>
                Child safety is our highest priority. All recommendations provided by Dozzy adhere to current safe sleeping guidelines from Red Nose Australia. We are committed to promoting sleep practices that prioritize your child's wellbeing and safety.
              </p>
              <p>
                Sleep deprivation affects not just children but entire families. Research shows that
                consistent, quality sleep is essential for:
              </p>
              <ul>
                <li>Healthy brain development in children</li>
                <li>Emotional regulation and behavior</li>
                <li>Immune system function</li>
                <li>Learning and memory consolidation</li>
                <li>Parent mental health and wellbeing</li>
              </ul>
              <p>
                By helping families establish healthy sleep patterns, we contribute to happier, healthier
                homes across Australia.
              </p>
            </div>
            <div className="col-lg-6 order-lg-1">
              <img src="/images/colin-maynard-CEEhmAGpYzE-unsplash.jpg" alt="Tired parent" className="img-fluid rounded-4 shadow" />
            </div>
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="py-3 bg-primary text-white">
        
      </section>
      
      {/* Evidence-Based Approach Section */}
      <section className="py-5">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <h2>The Dozzy Difference</h2>
              <h4>Evidence-Informed Methodology</h4>
              <p>
                Our sleep plans incorporate guidelines from trusted Australian resources including Triple P, Red Nose Australia, Raising Children Network, and Tresillian. This ensures that all recommendations are aligned with current best practices for child sleep health and safety.
              </p>
              <h4>Personalized Solutions</h4>
              <p>
                We recognize that every child is unique. Our AI technology analyzes multiple factors specific to your child to create a sleep plan tailored to their individual needs, rather than offering generic, one-size-fits-all advice.
              </p>
              <h4>Affordable Accessibility</h4>
              <p>
                Professional sleep consultants can cost hundreds or even thousands of dollars. Dozzy provides comparable personalized guidance at a fraction of the cost, making quality sleep support accessible to more Australian families.
              </p>
              <h4>Adaptable Plans</h4>
              <p>
                As your child grows and develops, their sleep needs change. Dozzy allows you to update your child's profile and receive adjusted recommendations that evolve with your child's changing sleep patterns and developmental milestones.
              </p>
            </div>
            <div className="col-lg-6">
              <img src="/images/jonathan-borba-RWgE9_lKj_Y-unsplash.jpg" alt="Sleeping baby" className="img-fluid rounded-4 shadow mt-4" />
            </div>
          </div>
        </div>
      </section>
      
      
      
      {/* CTA Section */}
      <section className="py-5 bg-light-red">
        <div className="container">
          <div className="text-center">
            <h2>Join the Dozzy Community</h2>
            <p className="mx-auto mb-4" style={{ maxWidth: '700px' }}>
              Whether you're navigating newborn nights or toddler transitions, Dozzy is here to help.
              We invite you to join our community and take the first step toward better sleep for your child and your entire family.
            </p>
            <Link to="/signup" className="btn btn-red btn-lg">
              Get Your Sleep Plan
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
